import React from "react";
import { UseFormReturn } from "react-hook-form";
import { ClienteFormValues } from "../ClienteFormSchema";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { FormattedTextField } from "@/components/form/FormattedTextField";
import { SelectField } from "@/components/form/SelectField";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import {
  PipelineFormSection,
  pipelineFieldGridClassCompact,
  usePipelineForm,
} from "@/components/form/PipelineFormContext";
import { PipelineFieldRow } from "@/components/form/pipelineFieldRow";
import { TEMPO_PROCURA_OPTIONS } from "@/lib/qualificacaoLabels";

interface QualificacaoStepProps {
  form: UseFormReturn<ClienteFormValues>;
}

const tipoBemOptions = [
  { value: "IMOVEL", label: "Imóvel" },
  { value: "VEICULO", label: "Veículo" },
  { value: "OUTROS", label: "Outros" },
];

const tempoProcuraOptions = [...TEMPO_PROCURA_OPTIONS];

const prioridadeOptions = [
  { value: "ALTA", label: "Alta" },
  { value: "MEDIA", label: "Média" },
  { value: "BAIXA", label: "Baixa" },
  { value: "APENAS_PESQUISANDO", label: "Apenas Pesquisando" },
];

export function QualificacaoStep({ form }: QualificacaoStepProps) {
  const temEntrada = form.watch("temEntrada");
  const tipoBem = form.watch("tipoBem");
  const { isPipeline } = usePipelineForm();
  const gridClass = pipelineFieldGridClassCompact(isPipeline);

  const temEntradaField = (
    <FormField
      control={form.control}
      name="temEntrada"
      render={({ field }) =>
        isPipeline ? (
          <FormItem className="flex flex-col p-0 space-y-0">
            <PipelineFieldRow label="Tem entrada?">
              <label className="flex items-center gap-2 text-[13px] text-gray-800 dark:text-gray-200 leading-8 cursor-pointer">
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                Sim
              </label>
            </PipelineFieldRow>
          </FormItem>
        ) : (
          <FormItem className="flex flex-row items-center space-x-2 space-y-0 mb-0">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <FormLabel className="text-sm font-medium cursor-pointer">
              Tem valor de entrada?
            </FormLabel>
          </FormItem>
        )
      }
    />
  );

  return (
    <div>
      {!isPipeline && (
        <div className="mb-6 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Dados de Qualificação</h3>
          {temEntradaField}
        </div>
      )}

      {!isPipeline && (
        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertDescription>
            Preencha todas as informações obrigatórias de qualificação do cliente para prosseguir.
          </AlertDescription>
        </Alert>
      )}

      <PipelineFormSection>
        <div className={gridClass}>
          {isPipeline && temEntradaField}

          <SelectField
            form={form}
            name="tipoBem"
            label="Tipo do Bem"
            options={tipoBemOptions}
            placeholder="Selecione"
            required
          />

          <FormattedTextField
            form={form}
            name="valorParcelaOrcamento"
            label={isPipeline ? "Parcela (orçamento)" : "Valor de Parcela dentro do Orçamento *"}
            formatter="moeda"
            placeholder="R$ 0,00"
            required
          />

          {temEntrada && (
            <FormattedTextField
              form={form}
              name="valorEntrada"
              label="Valor da Entrada"
              formatter="moeda"
              placeholder="R$ 0,00"
              required
            />
          )}

          <SelectField
            form={form}
            name="tempoProcura"
            label={isPipeline ? "Tempo procurando" : "Há quanto tempo está procurando? *"}
            options={tempoProcuraOptions}
            placeholder="Selecione"
            required
          />

          <SelectField
            form={form}
            name="prioridade"
            label="Prioridade"
            options={prioridadeOptions}
            placeholder="Selecione"
            required
          />

          {tipoBem === "IMOVEL" && (
            <FormattedTextField
              form={form}
              name="tempoAluguel"
              label={isPipeline ? "Tempo de aluguel" : "Há quanto tempo mora de aluguel? (opcional)"}
              formatter="texto"
              placeholder="Ex: 2 anos, 6 meses"
            />
          )}
        </div>
      </PipelineFormSection>

      {!isPipeline && !temEntrada && (
        <Alert className="border-blue-200 bg-blue-50 mt-6">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            Cliente não possui valor de entrada. Continue preenchendo os demais campos de qualificação.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
